<template>
  <div class="container-scheduling">
    <div class="container-scheduling__header">
      <div class="container-scheduling__header-left">
        <i class="el-icon-s-grid container-scheduling__header-icon" />
        <span class="container-scheduling__header-title">{{
          $l('btn_container_mgmt') || '分柜管理'
        }}</span>
        <span class="container-scheduling__header-badge">{{ shipmentNo }}</span>
      </div>

      <div class="container-scheduling__header-center">
        <div class="container-scheduling__stepper">
          <span class="container-scheduling__stepper-label">{{
            $l('section_alloc_progress') || '分配进度'
          }}</span>
          <div class="container-scheduling__stepper-track">
            <div
              class="container-scheduling__stepper-fill"
              :style="{ width: allocationProgress + '%' }"
            />
            <div
              v-for="m in milestones"
              :key="m.pct"
              class="container-scheduling__stepper-dot"
              :class="{ 'is-reached': allocationProgress >= m.pct }"
              :style="{ left: m.pct + '%' }"
            >
              <div class="container-scheduling__stepper-dot-inner" />
              <span class="container-scheduling__stepper-dot-label">
                {{ m.pct }}%
              </span>
            </div>
          </div>
          <span
            class="container-scheduling__stepper-value"
            :class="
              allocationProgress >= 100
                ? 'is-done'
                : allocationProgress >= 80
                ? 'is-good'
                : ''
            "
            >{{ allocationProgress }}%</span
          >
        </div>
      </div>

      <div class="container-scheduling__header-right">
        <button
          class="container-scheduling__btn"
          :disabled="!containerList.length"
          @click="smartAllocate"
        >
          <i class="el-icon-connection" />
          {{ $l('btn_smart_allocate') || '智能分配' }}
        </button>
        <button
          class="container-scheduling__btn container-scheduling__btn--primary container-scheduling__btn--spaced"
          @click="openCreateContainer"
        >
          <i class="el-icon-plus" /> {{ $l('btn_add_container') || '新增货柜' }}
        </button>
        <button
          class="container-scheduling__btn container-scheduling__btn--close container-scheduling__btn--spaced"
          @click="cancel"
        >
          <i class="el-icon-close" />
        </button>
      </div>
    </div>

    <div class="container-scheduling__body">
      <aside class="container-scheduling__pool">
        <PendingGoodsPool
          ref="pendingGoodsPool"
          :shipmentCode="shipmentCode"
          @assign="assign"
          @batch-assign="assignBatch"
          @group-assign="assignGroup"
          @stats-change="updatePoolStats"
        />
      </aside>

      <main class="container-scheduling__workspace">
        <div v-if="!containerList.length" class="container-scheduling__empty">
          <div class="container-scheduling__empty-icon">
            <i class="el-icon-box" style="font-size:40px;color:#cbd5e1;" />
          </div>
          <div class="container-scheduling__empty-title">
            {{ $l('tips_empty_container_title') || '开始分柜配载' }}
          </div>
          <div class="container-scheduling__empty-desc">
            {{
              $l('tips_empty_container_desc') ||
                '请先新增货柜，然后从左侧产品池分配产品到各货柜'
            }}
          </div>
          <button
            class="container-scheduling__btn container-scheduling__btn--primary container-scheduling__btn--top-spaced"
            @click="openCreateContainer"
          >
            <i class="el-icon-plus" />
            {{ $l('btn_add_first_container') || '新增第一个货柜' }}
          </button>
        </div>

        <template v-else>
          <ContainerTabs
            ref="containerTabs"
            :data="containerList"
            :activeCode="activeContainerCode"
            :shipmentCode="shipmentCode"
            @change="changeActiveContainer"
            @reload="onContainerTabsReload"
          />

          <div class="container-scheduling__panel">
            <EditContainerGoodsCard
              v-if="activeContainerCode"
              :shipmentContainerCode="activeContainerCode"
              :detail="activeContainerDetail"
              :loading="activeContainerDetailLoading"
              :isBooked="!!activeContainerItem.shipmentBookingCode"
              @create-booking="openCreateBooking"
              @append-booking="openAppendBooking"
              @reload="onContainerGoodsReload"
            />
            <EditContainerGoods
              v-if="activeContainerCode"
              :detail="activeContainerDetail"
              :loading="activeContainerDetailLoading"
              :groupFields="groupFields"
              :groupableFields="groupableFields"
              :isBooked="!!activeContainerItem.shipmentBookingCode"
              @change-group-fields="changeGroupFields"
              @reload="onContainerGoodsReload"
            />
          </div>

          <ContainerSummary :data="containerList" />
        </template>
      </main>
    </div>

    <EditContainer
      :visible.sync="createVisible"
      :shipmentContainerCode="''"
      :shipmentCode="shipmentCode"
      @reload="onCreateContainerReload"
    />
    <edit-booking
      :type="editBookingOpts.type"
      :visible.sync="editBookingOpts.visible"
      :shipmentCode="shipmentCode"
      :shipmentBookingCode="editBookingOpts.shipmentBookingCode"
      :listContainer="editBookingOpts.listContainer"
      @reload="onBookingReload"
    />
    <append-booking-container
      :visible.sync="appendBookingOpts.visible"
      :shipmentCode="shipmentCode"
      :shipmentContainerCode="appendBookingOpts.shipmentContainerCode"
      @reload="onBookingReload"
    />
  </div>
</template>

<script>
import EditLoading from '@/mixins/edit-loading'
import PendingGoodsPool from '../pending-goods-pool/index.vue'
import EditContainerGoodsCard from '../edit-container-goods-card/index.vue'
import EditContainerGoods from '../edit-container-goods/index.vue'
import ContainerTabs from './container-tabs.vue'
import ContainerSummary from './container-summary.vue'
import EditContainer from '../edit-container/index.vue'
import EditBooking from '../edit-booking/index.vue'
import AppendBookingContainer from '../append-booking-container/index.vue'
import { subtract, divide, multiply, round } from '@/libs/util.number'
import editContainerGoodsColumns from '../edit-container-goods/table-columns'

export default {
  components: {
    PendingGoodsPool,
    EditContainerGoodsCard,
    EditContainerGoods,
    ContainerTabs,
    ContainerSummary,
    EditContainer,
    EditBooking,
    AppendBookingContainer,
  },
  mixins: [EditLoading],
  props: {
    shipmentCode: { type: String, default: '' },
    shipmentNo: { type: String, default: '' },
  },
  data() {
    return {
      milestones: [{ pct: 25 }, { pct: 50 }, { pct: 75 }, { pct: 100 }],
      containerList: [],
      activeContainerCode: '',
      activeContainerDetail: {},
      activeContainerDetailLoading: false,
      createVisible: false,
      editBookingOpts: {
        visible: false,
        type: 'add',
        shipmentBookingCode: '',
        listContainer: [],
      },
      appendBookingOpts: {
        visible: false,
        shipmentContainerCode: '',
      },
      groupFields: [],
      poolStats: {
        totalCtnQty: 0,
        remainCtnQty: 0,
      },
    }
  },
  computed: {
    allocationProgress() {
      const total = this.poolStats.totalCtnQty || 0
      if (!total) return 0
      return round(
        multiply(
          divide(subtract(total, this.poolStats.remainCtnQty), total),
          100
        ),
        0
      )
    },
    activeContainerItem() {
      return (
        this.containerList.find(
          (item) => item.shipmentContainerCode === this.activeContainerCode
        ) || {}
      )
    },
    groupableFields() {
      return editContainerGoodsColumns({
        $l: this.$l.bind(this),
        isBooked: !!this.activeContainerItem.shipmentBookingCode,
      })
        .filter(
          (column) =>
            column.group === true && column.field && column.visible !== false
        )
        .map((column) => ({
          key: column.field,
          label: column.title,
        }))
    },
  },
  mounted() {
    this.getContainerList()
  },
  methods: {
    cancel() {
      this.$emit('close')
    },
    changeActiveContainer(code) {
      if (code === this.activeContainerCode) return

      this.activeContainerCode = code
      this.groupFields = []
      this.activeContainerDetail = {}
      this.getActiveContainerDetail()
    },
    async smartAllocate() {
      if (!this.containerList.length) return

      const confirm = await this.$confirm(
        this.$l('tips_confirm_smart_allocate') ||
          '确认按系统规则将当前剩余未分配产品批量分配到现有货柜吗？',
        this.$l('tips_confirm_title') || '提示',
        {
          type: 'warning',
        }
      ).catch(() => null)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomShipmentContainerSchedulingApi({
          shipmentCode: this.shipmentCode,
        })
        .catch(() => null)
      this.loadingClose()
      if (!res) return

      this.$message({
        type: 'success',
        message: res.msg || this.$l('message_assign_ok') || '分配成功',
      })
      await this.refreshSchedulingData({ reloadParent: true })
    },
    changeGroupFields(fields = []) {
      this.groupFields = this.filterGroupFields(fields)
    },
    filterGroupFields(fields = []) {
      const groupableKeys = this.groupableFields.map((field) => field.key)
      return fields.filter((field) => groupableKeys.includes(field))
    },
    updatePoolStats(stats = {}) {
      this.poolStats = {
        totalCtnQty: Number(stats.totalCtnQty || 0),
        remainCtnQty: Number(stats.remainCtnQty || 0),
      }
    },
    reloadPendingGoodsPool() {
      if (this.$refs.pendingGoodsPool) {
        return this.$refs.pendingGoodsPool.reload()
      }
      return Promise.resolve()
    },
    openCreateContainer() {
      if (this.$refs.containerTabs) {
        this.$refs.containerTabs.openCreateContainer()
        return
      }
      this.createVisible = true
    },
    async onCreateContainerReload() {
      await this.refreshSchedulingData({ reloadParent: true })
    },
    async onContainerGoodsReload() {
      await this.refreshSchedulingData({ reloadParent: true })
    },
    async onContainerTabsReload() {
      await this.refreshSchedulingData({ reloadParent: true })
    },
    openCreateBooking(detail = {}) {
      if (!detail.shipmentContainerCode) return

      this.editBookingOpts = {
        visible: true,
        type: 'add',
        shipmentBookingCode: '',
        listContainer: [
          {
            shipmentContainerCode: detail.shipmentContainerCode,
            editContainerNo: detail.editContainerNo,
            containerName: detail.containerName,
            containerBoxQty: detail.containerBoxQty,
            containerVolume: detail.containerVolume,
            containerGoodsQty: detail.containerGoodsQty,
            containerGrossWeight: detail.containerGrossWeight,
            containerNetWeight: detail.containerNetWeight,
            containerWeight: detail.containerWeight,
            totalWeight: detail.totalWeight,
            containerBookingVolume: detail.containerBookingVolume,
            containerCostAmount: detail.containerCostAmount,
            containerSalePriceAmount: detail.containerSalePriceAmount,
            costCurrencyUnit:
              detail.costCurrencyUnitName || detail.costCurrencyUnit,
            salesCurrencyUnit:
              detail.salesCurrencyUnitName || detail.salesCurrencyUnit,
            shipmentBookingContainerCode: '',
            bookingNo: '',
            customsNo: '',
          },
        ],
      }
    },
    openAppendBooking(detail = {}) {
      if (!detail.shipmentContainerCode) return

      this.appendBookingOpts = {
        visible: true,
        shipmentContainerCode: detail.shipmentContainerCode,
      }
    },
    async onBookingReload() {
      await this.refreshSchedulingData({ reloadParent: true })
    },
    async refreshSchedulingData({ reloadParent = false } = {}) {
      await Promise.all([
        this.getContainerList(),
        this.reloadPendingGoodsPool(),
      ])
      if (reloadParent) {
        this.$emit('reload')
      }
    },
    async getActiveContainerDetail() {
      const shipmentContainerCode = this.activeContainerCode

      if (!shipmentContainerCode) {
        this.activeContainerDetail = {}
        this.activeContainerDetailLoading = false
        return null
      }

      this.activeContainerDetail = {}
      this.activeContainerDetailLoading = true
      const res = await this.$api
        .ecomShipmentContainerDetailApi({
          shipmentContainerCode,
        })
        .catch(() => null)

      if (shipmentContainerCode !== this.activeContainerCode) {
        return null
      }

      this.activeContainerDetailLoading = false
      if (!res) {
        this.activeContainerDetail = {}
        return null
      }

      this.activeContainerDetail = res.body || {}
      return this.activeContainerDetail
    },
    async getContainerList() {
      if (!this.shipmentCode) {
        this.containerList = []
        this.activeContainerCode = ''
        this.activeContainerDetail = {}
        this.activeContainerDetailLoading = false
        return
      }

      const res = await this.$api
        .ecomShipmentContainerListApi({
          shipmentCode: this.shipmentCode,
        })
        .catch(() => null)
      if (!res) return
      this.containerList = res.body?.data || res.body || []
      if (!this.activeContainerCode && this.containerList.length) {
        this.activeContainerCode = this.containerList[0].shipmentContainerCode
        this.groupFields = []
      }
      if (
        this.activeContainerCode &&
        !this.containerList.some(
          (item) => item.shipmentContainerCode === this.activeContainerCode
        )
      ) {
        const nextCode = this.containerList.length
          ? this.containerList[0].shipmentContainerCode
          : ''
        if (nextCode !== this.activeContainerCode) {
          this.groupFields = []
        }
        this.activeContainerCode = nextCode
      }
      if (!this.activeContainerCode) {
        this.activeContainerDetail = {}
        this.activeContainerDetailLoading = false
        return
      }
      await this.getActiveContainerDetail()
    },
    async assign(row) {
      if (!this.validateAssignableContainer()) return
      await this.assignRows([row.shipmentGoodsCode])
    },
    async assignBatch(rows = []) {
      await this.assignRowsWithConfirm(rows, 'confirmBatchAssignRows')
    },
    async assignGroup(rows = []) {
      await this.assignRowsWithConfirm(rows, 'confirmGroupAssignRows')
    },
    async assignRowsWithConfirm(rows = [], confirmMethod) {
      const listShipmentGoodsCode = [
        ...new Set(rows.map((row) => row.shipmentGoodsCode).filter(Boolean)),
      ]

      if (!listShipmentGoodsCode.length) return
      if (!this.validateAssignableContainer()) return

      const confirm = await this.$refs.pendingGoodsPool[confirmMethod](rows)
      if (!confirm) return

      await this.assignRows(listShipmentGoodsCode)
    },
    async assignRows(listShipmentGoodsCode = []) {
      if (!listShipmentGoodsCode.length) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomShipmentContainerBatchAddGoodsApi({
          shipmentCode: this.shipmentCode,
          shipmentContainerCode: this.activeContainerCode,
          listShipmentGoodsCode,
        })
        .catch(() => null)
      this.loadingClose()

      if (!res) {
        await this.refreshSchedulingData()
        return
      }

      this.$message({
        type: 'success',
        message: res.msg || this.$l('message_assign_ok') || '分配成功',
      })
      await this.refreshSchedulingData()
    },
    validateAssignableContainer() {
      if (!this.activeContainerCode) {
        this.$message.warning(
          this.$l('tips_select_container_first') || '请先选择货柜'
        )
        return false
      }
      const activeItem = this.containerList.find(
        (item) => item.shipmentContainerCode === this.activeContainerCode
      )
      if (activeItem && activeItem.shipmentBookingCode) {
        this.$message.warning(
          this.$l('tips_container_booked_no_assign') ||
            '该货柜已关联订舱，无法继续分配产品'
        )
        return false
      }

      return true
    },
  },
}
</script>

<style lang="scss" scoped>
.container-scheduling {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8fafc;
  overflow: hidden;
}

/* Header */
.container-scheduling__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 16px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.container-scheduling__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.container-scheduling__header-icon {
  font-size: 16px;
  color: #409eff;
}
.container-scheduling__header-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}
.container-scheduling__header-badge {
  font-size: 10px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
  letter-spacing: 0.02em;
}
.container-scheduling__header-center {
  flex: 1;
  display: flex;
  justify-content: center;
}
.container-scheduling__stepper {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 280px;
}
.container-scheduling__stepper-label {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.container-scheduling__stepper-track {
  position: relative;
  flex: 1;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  max-width: 220px;
}
.container-scheduling__stepper-fill {
  height: 100%;
  background: linear-gradient(90deg, #409eff, #66b1ff);
  border-radius: 2px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}
.container-scheduling__stepper-dot {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.container-scheduling__stepper-dot-inner {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e2e8f0;
  border: 2px solid #cbd5e1;
  transition: all 0.4s;
}
.container-scheduling__stepper-dot.is-reached
  .container-scheduling__stepper-dot-inner {
  background: #409eff;
  border-color: #409eff;
  box-shadow: 0 0 6px rgba(64, 158, 255, 0.4);
}
.container-scheduling__stepper-dot-label {
  position: absolute;
  top: 14px;
  font-size: 9px;
  color: #475569;
  white-space: nowrap;
  font-weight: 500;
}
.container-scheduling__stepper-value {
  font-size: 14px;
  font-weight: 700;
  color: #94a3b8;
  min-width: 40px;
  text-align: right;
  font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
  white-space: nowrap;
  &.is-good {
    color: #34d399;
  }
  &.is-done {
    color: #409eff;
  }
}
.container-scheduling__header-right {
  display: flex;
  align-items: center;
}

.container-scheduling__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #1e293b;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &--primary {
    background: #ecf5ff;
    border-color: #409eff;
    color: #409eff;
    &:hover:not(:disabled) {
      background: #d9ecff;
      border-color: #337ecc;
    }
  }
  &--close {
    border: none;
    color: #94a3b8;
    padding: 0 6px;
    i {
      font-size: 14px;
    }
    &:hover {
      color: #475569;
      background: #f1f5f9;
    }
  }

  .container-scheduling__btn--close {
    width: 30px;
    min-width: 30px;
  }
}
.container-scheduling__btn--spaced {
  margin-left: 8px;
}
.container-scheduling__btn--top-spaced {
  margin-top: 12px;
}

/* Body */
.container-scheduling__body {
  flex: 1;
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #fff;
  overflow: hidden;
  min-height: 0;
}

/* Pool */
.container-scheduling__pool {
  flex: 0 0 60%;
  max-width: 60%;
  min-width: 300px;
  overflow: hidden;
}

/* Workspace */
.container-scheduling__workspace {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Empty state */
.container-scheduling__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  gap: 12px;
  min-height: 400px;
}
.container-scheduling__empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.container-scheduling__empty-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}
.container-scheduling__empty-desc {
  margin: 0 0 8px;
  font-size: 12px;
  color: #94a3b8;
}

.container-scheduling__panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}
</style>
